'use client';
import React, { forwardRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconRadio, IconRadioActive } from '@repo/svgs';
import { CheckboxIcon } from './CheckboxIcon';

export type SelectionInputType = 'checkbox' | 'radio';
export type SelectionInputVariant = 'default' | 'card';

export interface SelectionInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  type: SelectionInputType;
  selected?: boolean;
  children: React.ReactNode;
  onChange?: (selected: boolean, value?: string) => void;
  wrapperClassName?: string;
  /**
   * When true, allows clicking an already-selected radio option to de-select it.
   * Default: false (radio selection is "sticky").
   */
  allowDeselect?: boolean;
  /**
   * Visual variant:
   * - default: compact pill (existing behavior)
   * - card: full-width bordered option row with selected ring/bg (used in application profile)
   */
  variant?: SelectionInputVariant;
}

export const SelectionInput = forwardRef<HTMLInputElement, SelectionInputProps>(
  function SelectionInput(
    {
      type,
      selected = false,
      children,
      onChange,
      wrapperClassName,
      variant = 'default',
      allowDeselect = false,
      className,
      disabled = false,
      value,
      name,
      ...inputProps
    },
    ref,
  ) {
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const handleClick = () => {
      if (disabled) return;

      if (type === 'radio' && selected) {
        if (!allowDeselect) return;
        onChange?.(false, typeof value === 'string' ? value : String(value));
        return;
      }

      const newSelected = type === 'radio' ? true : !selected;
      onChange?.(newSelected, typeof value === 'string' ? value : String(value));
    };

    const handleMouseDown = () => {
      if (!disabled) {
        setIsPressed(true);
      }
    };

    const handleMouseUp = () => {
      setIsPressed(false);
    };

    const handleMouseEnter = () => {
      if (!disabled) {
        setIsHovered(true);
      }
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setIsPressed(false);
    };

    // Determine the background color based on state
    const getBackgroundClass = () => {
      if (disabled) {
        return 'bg-secondary cursor-not-allowed';
      }

      if (variant === 'card' && selected) {
        return 'bg-active cursor-pointer';
      }

      if (isPressed) {
        return 'bg-primary-press cursor-pointer';
      }

      if (isHovered) {
        return 'bg-primary-hover cursor-pointer';
      }

      return 'bg-primary cursor-pointer';
    };

    // Render the appropriate icon based on type and selected state
    const renderIcon = () => {
      const iconClasses = 'w-4 h-4 shrink-0';

      if (type === 'checkbox') {
        return <CheckboxIcon checked={selected} className={cn(iconClasses, 'mt-1')} />;
      } else {
        return selected ? (
          <IconRadioActive className={cn(iconClasses, 'icon-accent')} />
        ) : (
          <IconRadio className={cn(iconClasses, 'icon-secondary')} />
        );
      }
    };

    return (
      <div className={cn('relative', wrapperClassName)}>
        {/* Hidden input for form integration and accessibility */}
        <input
          ref={ref}
          type={type}
          checked={selected}
          disabled={disabled}
          value={value}
          name={name}
          className="sr-only"
          onChange={e =>
            onChange?.(e.target.checked, typeof value === 'string' ? value : String(value))
          }
          {...inputProps}
        />

        {/* Visual component */}
        <div
          className={cn(
            // Layout
            'flex items-center gap-3',
            variant === 'card' ? 'px-4 py-4 rounded-2xl border' : 'px-2 py-3 rounded-xl',
            // Background and interactions
            getBackgroundClass(),
            // Width
            variant === 'card' ? 'w-full max-w-none' : 'w-full max-w-md',
            // Card borders + selected ring treatment
            variant === 'card'
              ? selected
                ? 'border-transparent ring-2 ring-inset ring-accent'
                : 'border-secondary'
              : null,
            className,
          )}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role={type === 'radio' ? 'radio' : 'checkbox'}
          aria-checked={selected}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={e => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {renderIcon()}
          <span className={cn('flex-1 text-body-2', disabled ? 'text-tertiary' : 'text-primary')}>
            {children}
          </span>
        </div>
      </div>
    );
  },
);

export default SelectionInput;
